<template>
  <loading v-if="!questionLoaded"></loading>
  <div v-else-if="question.private && (question.user.id !== userId && !userInvited)">
    <h1>Question is private</h1>

    <p>You are not able to view this question</p>
  </div>
  <div v-else-if="!question || (question.status == 'draft' && question.user.id !== userId)">
    <h1>Question not found</h1>

    <p>The collaboration question could not be found. It may have expired or been removed.</p>

    <p>
      Please
      <router-link :to="{name: 'Collaboration'}">click here to view all questions</router-link>.
    </p>
  </div>
  <div class="p-question" v-else>
    <page-title>Collaboration Hub</page-title>

    <section-block sticky-container actionsDropdown>
      <template slot="header">
        <base-button @click="$router.push({name: 'Collaboration'})" iconBefore="back">
          <span class="u-hide-mobile">Back to{{' '}}</span>All Questions
        </base-button>
      </template>

      <template slot="actions">
        <dropdown class="u-show-tablet-down">
          <dropdown-action
            @click="addResponseModal = true"
            v-if="question.created_user.id === userId && !isDraft"
          >Add Response</dropdown-action>
          <dropdown-action @click="onClickPin" v-if="userCanPin && !isDraft">{{pinText}}</dropdown-action>
          <dropdown-action @click="onClickInvite" v-if="canInvite">Invite</dropdown-action>
          <dropdown-action @click="onClickLeave" v-if="question.private && userInvited">Leave</dropdown-action>
          <dropdown-action
            @click="onClickFollow"
            v-if="!following && question.created_user.id != userId"
          >Follow</dropdown-action>
          <dropdown-action
            @click="onClickUnfollow"
            v-if="following && question.created_user.id != userId"
          >Unfollow</dropdown-action>
          <dropdown-action @click="onEditClick" v-if="userCanEdit">Edit</dropdown-action>
          <dropdown-action @click="onPublishClick" v-if="userCanEdit && isDraft">Publish</dropdown-action>
          <dropdown-action
            @click="onCloseClick"
            v-if="question.created_user.id == userId && !submissionClosed && !isDraft"
          >Close</dropdown-action>
          <dropdown-action
            @click="onDeleteClick"
            v-if="userCanDelete || question.created_user.id == userId"
          >Delete</dropdown-action>
        </dropdown>
        <div class="u-hide-tablet">
          <base-button
            @click="addResponseModal = true"
            iconBefore="comments"
            v-if="question.created_user.id == userId"
          >Add Response</base-button>
          <base-button
            @click="onClickPin"
            iconBefore="pin"
            v-if="userCanPin && !isDraft"
          >{{pinText}}</base-button>
          <base-button @click="onClickInvite" iconBefore="addUser" v-if="canInvite">Invite</base-button>
          <base-button
            @click="onClickLeave"
            iconBefore="addUser"
            v-if="question.private && userInvited"
          >Leave</base-button>
          <base-button
            @click="onClickFollow"
            iconBefore="view"
            v-if="!following && question.created_user.id != userId"
          >Follow</base-button>
          <base-button
            @click="onClickUnfollow"
            iconBefore="view"
            v-else-if="following && question.created_user.id != userId"
          >Unfollow</base-button>
          <base-button @click="onEditClick" iconBefore="edit" v-if="userCanEdit">Edit</base-button>
          <base-button
            :primary="true"
            @click="onPublishClick"
            iconBefore="edit"
            v-if="userCanEdit && isDraft"
          >Publish</base-button>
          <base-button
            @click="onCloseClick"
            iconBefore="cross"
            v-if="question.created_user.id == userId && !submissionClosed && !isDraft"
          >Close</base-button>
          <base-button
            @click="onDeleteClick"
            iconBefore="cross"
            v-if="userCanDelete || question.created_user == userId"
          >Delete</base-button>
        </div>
      </template>

      <div class="p-question__hero">
        <div class="row">
          <section class="col-xs-12 col-md-8">
            <inline-list withPadding>
              <li class="p-question__label uppercase">Question</li>
              <li v-if="question.private">
                <inline-label type="dark">Private</inline-label>
              </li>
              <li
                v-if="question.private && question.users && question.users.length"
                class="p-question__label"
              >
                {{question.users.length+1}} user
                <span v-if="question.users.length+1 !== 1">s</span>
              </li>
            </inline-list>
            <h2 class="p-question__title" v-if="question.title">{{question.title}}</h2>

            <content-expand :content="question" />

            <section v-if="latestResponse" class="p-question__response-list">
              <h3>Responses</h3>
              <base-question-response :response="latestResponse" />

              <p v-if="responses.length > 1">
                <router-link
                  :to="{name: 'QuestionResponses', params:{questionId: question.id}}"
                >View all responses</router-link>
              </p>
            </section>

            <footer class="p-question__hero-footer">
              <meta-row
                :show-user="false"
                :object="question"
                :show-private="false"
                :show-idea-count="false"
                :show-status="false"
                :show-response-count="false"
                :show-votes="false"
                :show-pinned="false"
              >
                <template slot="full">
                  <profile-card-mini :user="question.user" show-avatar show-name />
                </template>
              </meta-row>
            </footer>
          </section>
          <section class="col-xs-12 col-md-4 p-question__meta">
            <meta-box
              v-sticky="stickRule"
              sticky-offset="offset"
              sticky-side="both"
              on-stick="onStick"
              sticky-z-index="20"
            >
              <meta-box-row large :label="approvedIdeaCount == 1 ? 'Idea' : 'Ideas'">
                <span>{{approvedIdeaCount}}</span>
              </meta-box-row>
              <meta-box-row label="Question Added:">
                <span>{{formattedDate}}</span>
              </meta-box-row>
              <meta-box-row label="Closes for submission:">
                <span v-if="!submissionClosed">{{closeDateString}}</span>
                <span v-else class="error">Closed</span>
              </meta-box-row>
              <meta-box-row label="Private/Open:">
                <span>{{question.private ? 'Private' : 'Open'}}</span>
              </meta-box-row>
              <meta-box-row label="No. of Follows:">
                <span>{{question.favourite_count}}</span>
              </meta-box-row>

              <base-button
                :primary="true"
                iconBefore="lightbulb"
                :iconBeforeFill="false"
                block
                square
                @click="addIdea"
                v-if="!submissionClosed && UserHasPermission && !isDraft"
                class="p-question__submit"
              >Submit an Idea</base-button>
              <p class="ta-center" v-if="isDraft">
                <inline-label type="warning">This question is currently in draft mode</inline-label>
              </p>
            </meta-box>
          </section>
        </div>
      </div>

      <section class="ideas-list col-xs-12 col-md-8">
        <header class="row align-center">
          <div class="col-xs-4">
            <p class="idea-count">
              <icon name="lightbulb" :fill="null" />
              {{approvedIdeaCount || 0}} {{ideas && approvedIdeaCount == 1 ? 'idea' : 'ideas'}}
            </p>
          </div>
          <div class="col-xs-8 ta-right">
            <comment-sort @onSortChange="onSortChange" :allowVoting="question.voting" />
          </div>
        </header>

        <div>
          <ol class="listless c-idea-list" v-if="ideas.length > 0">
            <li
              v-for="idea in sortedIdeas"
              :key="idea.id"
              @click="viewIdea(idea)"
              :class="{implemented: idea.status === 'implemented', declined: idea.status === 'declined', review: idea.status === 'review'}"
            >
              <div v-if="canDisplayIdea(idea)">
                <meta-row :object="idea" :showAttachments="false" />
                <h3>
                  <span v-if="idea.status == 'implemented'" class="implemented-label">
                    <icon name="tick" />&nbsp;Implemented
                  </span>
                  <span v-if="idea.status == 'declined'" class="declined-label">
                    <icon name="cross" />&nbsp;Declined
                  </span>
                  <span v-if="idea.status == 'review'" class="review-label">
                    <icon name="search" />&nbsp;In review
                  </span>
                  <icon name="attachment" v-if="idea.files" class="c-icon c-icon--attach" />
                  {{idea.title}}
                </h3>
                <div v-html="$options.filters.truncate(idea.description, 100)" class="idea-desc"></div>
              </div>
            </li>
          </ol>

          <base-empty v-else-if="!isDraft">
            There are currently no ideas for this question.
            <br />Why not
            <router-link :to="ideaLink">submit one now</router-link>?
          </base-empty>

          <base-empty v-else>This question is currently in draft mode</base-empty>
        </div>
      </section>
    </section-block>
    <modal
      v-if="addResponseModal"
      class="ta-left"
      show-close-button
      @close="addResponseModal = false"
    >
      <h1 slot="header">Add a response</h1>
      <form method="post" v-on:submit.prevent>
        <form-field
          full-width
          name="reason"
          type="textarea"
          v-model="responseText"
          required
          v-validate="{required: true}"
          data-vv-as="response"
          :error="errors.first('response')"
        ></form-field>

        <base-button type="submit" @click="onAddResponse">Add Response</base-button>
      </form>
    </modal>
  </div>
</template>

<script>
import Sticky from "vue-sticky-directive";
import api from "@/api";
import IdeaList from "@/components/IdeaList";
import MetaBox from "@/components/MetaBox";
import MetaBoxRow from "@/components/MetaBoxRow";
import MetaRow from "@/components/MetaRow";
import { mapState } from "vuex";
import { BaseEmpty, InlineLabel } from "@/components/elements";
import { ContentExpand } from "@/components/Containers";
import { InlineList } from "@/components/Lists";
import { CommentSort } from "@/components/Comments";
import UserHasPermission from "@/mixins/UserHasPermission";
import BaseQuestionResponse from "@/components/BaseQuestionResponse";
import ProfileCardMini from "@/components/contentElements/ProfileCardMini";

const Dropdown = () => import("@/components/Dropdown");
const DropdownAction = () => import("@/components/DropdownAction");

export default {
  name: "question-show",

  async mounted() {
    const { questionId } = this.$route.params;

    try {
      await this.$store.dispatch("Questions/read", questionId);
      await this.$store.dispatch("Ideas/list", { questionId, isFlat: true });
      await this.$store.dispatch("Questions/listResponses", {
        questionId,
        isFlat: true
      });
      this.questionLoaded = true;
    } catch (err) {
      this.questionLoaded = true;
    }
  },

  data() {
    return {
      questionLoaded: false,
      submissionClosed: false,
      sortField: "vote_count",
      sortDir: "desc",
      addResponseModal: false,
      responseText: null
    };
  },

  beforeMount() {
    if (this.$route.params.questionId) {
      this.id = this.$route.params.questionId;
    }
  },

  methods: {
    onAddResponse() {
      const vm = this;
      this.$validator.validate().then(result => {
        if (!result) {
          vm.validationFailed = true;
        } else {
          vm.validationFailed = false;
          // send a review
          vm.$store
            .dispatch("Questions/respond", this.responseText)
            .then(resp => {
              vm.responseText = null;
              vm.addResponseModal = false;
              vm.$store.dispatch(
                "notifications/create",
                {
                  body: `Your response has been added`,
                  type: "success"
                },
                { root: true }
              );
            });
          // this.$emit("update", this.el);
        }
      });
    },
    canDisplayIdea(idea) {
      // by default show the idea, unless we are in review, in which case only question owner
      // and idea submitted should be able to view it
      const userId = this.$store.state.auth.currentUser.id;

      if (idea.status === "review") {
        if (
          this.question.created_user.id === userId ||
          idea.created_user === userId
        ) {
          return true;
        }
        return false;
      }
      return true;
    },
    onSortChange(order) {
      const { direction, field } = order;
      this.sortField = field;
      this.sortDir = direction;
    },
    onEditClick() {
      this.$router.push({
        name: "QuestionEdit",
        params: { questionId: this.id }
      });
    },
    onPublishClick() {
      // We need to validate all of the required data before we can publish
      const payload = {
        id: this.question.id,
        title: this.question.title,
        description: this.question.description,
        private: this.question.private,
        voting: this.question.voting,
        end_date: this.question.end_date,
        status: this.question.status,
        interestgroup_ids: this.question.interestgroups
          ? this.question.interestgroups.map(group => group.id)
          : [],
        file_ids: this.question.file_ids,
        user_ids: this.question.users
          ? this.question.users.map(user => user.id)
          : []
      };

      const required = ["title", "description", "end_date"];

      required.forEach(field => {
        if (!payload[field] || payload[field] === null) {
          this.$store.dispatch("notifications/create", {
            body: "Please edit the question and complete all details",
            type: "danger"
          });
          return false;
        }
      });
      this.$store
        .dispatch("Questions/update", payload)
        .then(resp => {
          this.$store.dispatch("Questions/publish", this.id);
        })
        .catch(err => {
          console.log("err", { ...err });
        });
    },
    onCloseClick() {
      this.$store.dispatch("createDialog", {
        confirm: () => {
          this.$store
            .dispatch("Questions/close", this.id)
            .then(resp => {})
            .catch(err => {});
        }
      });
    },
    addIdea() {
      this.$router.push({
        name: "IdeaSubmit",
        params: { questionId: this.id }
      });
    },

    onClickPin() {
      // pin a question
      const action = this.question.pinned ? "Questions/unpin" : "Questions/pin";
      const confirm = this.question.pinned ? "unpinned" : "pinned";
      this.$store
        .dispatch(action, this.id)
        .then(resp => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `The question has been ${confirm}`,
              type: "success"
            },
            { root: true }
          );
        })
        .catch(err => {
          console.log({ ...err });
          this.$store.dispatch(
            "notifications/create",
            {
              body: `There was a problem - ${err}`,
              type: "error"
            },
            { root: true }
          );
        });
    },

    onClickFollow() {
      this.$store
        .dispatch("Questions/follow", this.id)
        .then(resp => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `You are now following this question`,
              type: "success"
            },
            { root: true }
          );
          this.question.user_favourite = true;
        })
        .catch(err => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `There was a problem - ${err}`,
              type: "error"
            },
            { root: true }
          );
        });
    },

    onClickUnfollow() {
      this.$store
        .dispatch("Questions/unfollow", this.id)
        .then(resp => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `You are no longer following this question`,
              type: "success"
            },
            { root: true }
          );
          this.question.user_favourite = false;
        })
        .catch(err => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `There was a problem - ${err}`,
              type: "error"
            },
            { root: true }
          );
        });
    },

    onClickLeave() {
      this.$store
        .dispatch("Questions/leave", this.id)
        .then(resp => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `You are no longer invited to this question`,
              type: "success"
            },
            { root: true }
          );
          window.location.reload();
        })
        .catch(err => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `There was a problem - ${err}`,
              type: "error"
            },
            { root: true }
          );
        });
    },

    onClickInvite() {
      this.$router.push(`/collaboration/question/${this.id}/invite`);
    },

    onDeleteClick() {
      const vm = this;
      this.$store.dispatch("createDialog", {
        confirm: () => {
          this.$store
            .dispatch("Questions/delete", this.id)
            .then(resp => {
              vm.$router.push({ name: "Collaboration" });
            })
            .catch(err => {});
        }
      });
    },

    viewIdea(idea) {
      this.$router.push(`/collaboration/question/${this.id}/idea/${idea.id}`);
    }
  },

  computed: {
    ...mapState("Questions", ["question", "responses"]),
    ...mapState("Ideas", ["ideas"]),

    approvedIdeaCount() {
      const approved = this.ideas.filter(idea => idea.status !== 'review');

      return approved.length;
    },

    latestResponse() {
      return this.responses.slice(0)[0]; //immutable ftw
    },

    canInvite() {
      return (
        !this.question.private ||
        (this.question.private && this.question.created_user.id === this.userId)
      );
    },

    isDraft() {
      return this.question.status == "draft";
    },

    pinText() {
      if (this.question.pinned) {
        return "Unpin";
      }

      return "Pin";
    },

    userCanDelete() {
      return this.userHasPermission("delete_questions");
    },

    userCanPin() {
      return this.userHasPermission("pin_question");
    },

    /**
     * If we are the question owner, or if we have permission to edit questions
     */
    userCanEdit() {
      if (
        this.question.created_user.id === this.userId ||
        this.userHasPermission("add_edit_publish_questions")
      ) {
        return true;
      }
    },

    userId() {
      if (!this.$store.state.auth.currentUser.id) {
        this.$store.dispatch("auth/listCurrentUser").then(resp => {
          return this.$store.state.auth.currentUser.id;
        });
      } else {
        return this.$store.state.auth.currentUser.id;
      }
    },

    following() {
      return this.question.user_favourite;
    },

    userInvited() {
      if (!this.question.users) {
        return false;
      }
      const users = this.question.users.filter(
        user => user.id == this.$store.state.auth.currentUser.id
      );

      return users && users.length;
    },

    UserHasPermission() {
      return this.userHasPermission("add_ideas");
    },

    sortedIdeas() {
      const { sortField, sortDir } = this;
      let sorted = [];

      if (sortDir === "asc") {
        sorted = this.ideas.sort((a, b) => {
          return a[sortField] - b[sortField];
        });
      } else {
        sorted = this.ideas.sort((a, b) => {
          return b[sortField] - a[sortField];
        });
      }

      return sorted;
    },

    ideaLink() {
      return {
        name: "IdeaSubmit",
        params: { questionId: this.$route.params.questionId }
      };
    },

    stickRule() {
      return window.outerWidth >= 1024;
    },

    offset() {
      return {
        top: 150,
        bottom: 50
      };
    },

    formattedDate() {
      const returnDate = this.$dayjs().to(
        this.$dayjs(this.question.created_time)
      );
      return returnDate;
    },

    closeDateString() {

      const closeDate = this.$dayjs(this.question.end_date).format(
        "YYYY-MM-DD 00:00:00"
      );
      const today = this.$dayjs(new Date()).format("YYYY-MM-DD 00:00:00");
      if (closeDate < today || this.question.status == "closed") {
        this.submissionClosed = true;
      }
      return this.$dayjs(this.question.end_date).format("DD / MM / YYYY");
    }
  },

  components: {
    IdeaList,
    MetaBox,
    MetaBoxRow,
    MetaRow,
    BaseEmpty,
    CommentSort,
    InlineLabel,
    InlineList,
    ContentExpand,
    Dropdown,
    DropdownAction,
    BaseQuestionResponse,
    ProfileCardMini
  },
  mixins: [UserHasPermission],
  directives: { Sticky }
};
</script>

<style lang="scss">
.p-question {

  &__submit {
    position: fixed;
    bottom: 50px;
    bottom: calc(env(safe-area-inset-bottom) + 50px);
    left: 0;
    z-index: 25;

    @media (min-width: 1024px) {
      position: relative;
      bottom: auto;
    }
  }
  &__hero {
    position: relative;
  }

  &__meta {
    @media screen and (min-width: 1024px) {
      max-height: 1px;
    }
  }
}
.c-idea-list {
  > li {
    border-top: 1px solid $colour-nhs-pale-grey;
    padding: 20px 0;
    @media (min-width: 768px) {
      padding: 20px;
    }
    cursor: pointer;

    h3 {
      margin-top: 20px;
      margin-bottom: 0;
      padding-bottom: 0;
      font-weight: normal;
      font-size: 16px;
      line-height: 23px;
      font-family: $font-frutiger-light;
      color: #202020;
    }

    &:hover {
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.11);
    }

    &.implemented {
      background: rgba(22, 164, 70, 0.05);

      .implemented-label {
        color: #16a446;
        font-weight: bold;
        font-family: $font-family-display;

        svg {
          height: 12px;
        }
      }
    }

    &.declined {
      .declined-label {
        color: red;
        font-weight: bold;
        font-family: $font-family-display;

        svg {
          height: 12px;
        }
      }
    }
    &.review {
      .review-label {
        color: $colour-nhs-orange;
        font-weight: bold;
        font-family: $font-family-display;

        svg {
          height: 12px;
        }
      }
    }
  }
}

.p-question {
  &__hero {
    background: #f0f5fb;
    background-position: left 1.5em;
    padding: 1em;
    margin: -10px -10px 10px;
    @media screen and (min-width: 1024px) {
      background: url(/assets/qmark.svg) no-repeat #f0f5fb;
      margin: -1em -1em 1em;
      padding: 4em 6em 4em 12em;
    }
  }

  &__response-list {
    border-top: 1px solid $colour-nhs-pale-grey;
    h3 {
      color: $colour-nhs-mid-grey;
      text-transform: uppercase;
      font-size: 16px;
    }
  }

  &__hero-footer {
    border-top: 1px solid #e4e4e4;
  }

  &__label {
    font-size: 16px;
    color: $colour-nhs-blue;
    margin-top: 0;

    &.uppercase {
      text-transform: uppercase;
    }
  }

  &__title {
    color: #202020;
    font-size: 20px;
    font-weight: normal;
    margin-top: 0;
  }

  &__description {
    line-height: 1.25;
  }

  .ideas-list {
    header {
      padding: 20px 0;
    }
  }
}

.idea-desc {
  font-size: 14px;
  color: #697884;

  > p {
    margin-top: 0;
  }
}

.error {
  color: $colour-nhs-emergency-red;
}

.align-center {
  align-items: center;
}

.idea-count {
  font-weight: bold;
  color: $colour-nhs-core-blue;
  display: inline-flex;
  align-items: center;

  svg {
    margin-right: 5px;
    path {
      stroke: $colour-nhs-core-blue;
    }
  }
}

.ta-right {
  .c-filter-bar {
    justify-content: flex-end;

    &__sort {
      margin-right: 0;
    }
  }
}

.c-icon {
  height: 12px;
  &--attach {
    path {
      fill: $colour-nhs-dark-grey;
    }
  }
}
</style>
